import { memo } from "react";
import {
  Badge,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Circle,
  HStack,
  Icon,
  Image,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FiCheck, FiChevronLeft } from "react-icons/fi";
import { Link as RouterLink } from "react-router-dom";
import {
  formatFundCurrency,
  getFundRedemptionNavLabel,
  getFundSubscriptionNavLabel,
} from "../fundCardUtils";
import { formatNumber, formatPercent, getFundTypeColor } from "./helpers";
import { Step } from "./types";

type InvestmentHeaderProps = {
  activeStep: Step;
  borderColor: string;
  cancelNav: number;
  cardBg: string;
  fund: any;
  fundLogoSrc: string | null;
  heroBgGradient: string;
  investorTotalAsset: number;
  investorTotalUnits: number;
  isInvestorProfileLoading: boolean;
  onBackToDetails: () => void;
  stepActiveBg: string;
  stepDoneBg: string;
  stepInactiveBg: string;
  steps: Array<{ label: string }>;
  subscriptionNav: number;
  subtitleColor: string;
  textColor: string;
};

export const InvestmentHeader = memo(
  ({
    activeStep,
    borderColor,
    cancelNav,
    cardBg,
    fund,
    fundLogoSrc,
    heroBgGradient,
    investorTotalAsset,
    investorTotalUnits,
    isInvestorProfileLoading,
    onBackToDetails,
    stepActiveBg,
    stepDoneBg,
    stepInactiveBg,
    steps,
    subscriptionNav,
    subtitleColor,
    textColor,
  }: InvestmentHeaderProps) => {
    const subscriptionNavLabel = getFundSubscriptionNavLabel(
      fund?.fundRegisterCode,
    );
    const redemptionNavLabel = getFundRedemptionNavLabel(
      fund?.fundRegisterCode,
    );

    return (
      <>
        <Breadcrumb
          spacing="6px"
          separator={
            <Icon
              verticalAlign="middle"
              as={FiChevronLeft as any}
              color="gray.400"
              boxSize={3}
            />
          }
          mb="50px"
        >
          <BreadcrumbItem>
            <BreadcrumbLink
              as={RouterLink}
              to="/"
              color={subtitleColor}
              fontSize="sm"
            >
              خانه
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink
              as={RouterLink}
              to="/funds"
              color={subtitleColor}
              fontSize="sm"
            >
              صندوق‌ها
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink
              as={RouterLink}
              to={`/funds/${fund.fundRegisterCode}`}
              color={subtitleColor}
              fontSize="sm"
            >
              {fund.fundName.trim()}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink
              color={textColor}
              fontWeight="semibold"
              fontSize="sm"
            >
              خرید آسان
            </BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>

        <HStack justify="center" spacing={4} mb="20px">
          {steps.map((item, index) => {
            const stepNum = (index + 1) as Step;
            const isActive = activeStep === stepNum;
            const isDone = activeStep > stepNum;
            const isClickable = isDone;

            return (
              <HStack key={item.label} spacing={2}>
                <HStack
                  spacing={2}
                  cursor={isClickable ? "pointer" : "default"}
                  onClick={isClickable ? onBackToDetails : undefined}
                  opacity={isActive || isDone ? 1 : 0.5}
                  _hover={isClickable ? { opacity: 0.8 } : {}}
                  transition="opacity 0.2s"
                >
                  <Circle
                    size="28px"
                    bg={
                      isDone
                        ? stepDoneBg
                        : isActive
                          ? stepActiveBg
                          : stepInactiveBg
                    }
                    color="white"
                    fontSize="xs"
                    fontWeight="bold"
                    transition="all 0.3s"
                  >
                    {isDone ? (
                      <Icon
                        verticalAlign="middle"
                        as={FiCheck as any}
                        boxSize={3}
                      />
                    ) : (
                      <Text>{stepNum}</Text>
                    )}
                  </Circle>
                  <Text
                    fontSize="sm"
                    fontWeight={isActive ? "bold" : "normal"}
                    color={isActive || isDone ? textColor : subtitleColor}
                    transition="all 0.3s"
                  >
                    {item.label}
                  </Text>
                </HStack>
                {index < steps.length - 1 && (
                  <Box
                    w="32px"
                    h="1.5px"
                    bg={isDone ? stepDoneBg : borderColor}
                    borderRadius="full"
                    transition="all 0.3s"
                  />
                )}
              </HStack>
            );
          })}
        </HStack>

        <Box
          bg={cardBg}
          borderRadius="2xl"
          border="1px solid"
          borderColor={borderColor}
          overflow="hidden"
          mb="10px"
        >
          <Box
            p={4}
            position="relative"
            _before={{
              content: '""',
              position: "absolute",
              inset: 0,
              background: heroBgGradient,
              zIndex: 0,
            }}
          >
            <VStack align="stretch" spacing={3} position="relative" zIndex={1}>
              <VStack align="center" spacing={0.5} mb="10px">
                <VStack spacing={2}>
                  {fundLogoSrc ? (
                    <Image
                      src={fundLogoSrc}
                      alt={fund.fundName.trim()}
                      boxSize="100px"
                      objectFit="contain"
                    />
                  ) : null}
                  <Text fontWeight="bold" fontSize="lg" color={textColor}>
                    {fund.fundName.trim()}
                  </Text>
                  <Badge
                    colorScheme={getFundTypeColor(fund.fundTypeTitle)}
                    fontSize="xs"
                    px={2}
                    borderRadius="full"
                  >
                    {fund.fundTypeTitle.trim()}
                  </Badge>
                </VStack>
              </VStack>

              <SimpleGrid columns={3} spacing={4}>
                <VStack spacing={0} align="center">
                  <Text fontSize="xs" color={subtitleColor}>
                    {subscriptionNavLabel}
                  </Text>
                  <Text fontSize="md" fontWeight="bold" color="green.500">
                    {formatNumber(subscriptionNav)}
                  </Text>
                </VStack>
                <VStack spacing={0} align="center">
                  <Text fontSize="xs" color={subtitleColor}>
                    بازدهی سالانه
                  </Text>
                  <Text
                    fontSize="md"
                    fontWeight="bold"
                    color={fund.annualReturn >= 0 ? "green.500" : "red.500"}
                  >
                    {fund.annualReturn >= 0 ? "+" : ""}
                    {formatPercent(fund.annualReturn)}%
                  </Text>
                </VStack>
                <VStack spacing={0} align="center">
                  <Text fontSize="xs" color={subtitleColor}>
                    {redemptionNavLabel}
                  </Text>
                  <Text fontSize="md" fontWeight="bold" color="red.400">
                    {formatNumber(cancelNav)}
                  </Text>
                </VStack>
              </SimpleGrid>

              <SimpleGrid columns={2} spacing={4}>
                <VStack spacing={0} align="center">
                  <Text fontSize="sm" color={subtitleColor}>
                    تعداد واحد
                  </Text>
                  <Text fontSize="lg" fontWeight="bold" color={textColor}>
                    {isInvestorProfileLoading
                      ? "..."
                      : `${formatNumber(investorTotalUnits)} واحد`}
                  </Text>
                </VStack>
                <VStack spacing={0} align="center">
                  <Text fontSize="sm" color={subtitleColor}>
                    دارایی
                  </Text>
                  <Text fontSize="lg" fontWeight="bold" color={textColor}>
                    {isInvestorProfileLoading
                      ? "..."
                      : `${formatFundCurrency(investorTotalAsset)} ریال`}
                  </Text>
                </VStack>
              </SimpleGrid>
            </VStack>
          </Box>
        </Box>
      </>
    );
  },
);

InvestmentHeader.displayName = "InvestmentHeader";
